import React, { useState, useEffect } from 'react';
import { 
    CreditCardIcon, 
    ArrowPathIcon, 
    XMarkIcon, 
    CalendarDaysIcon,
    InformationCircleIcon
} from '@heroicons/react/24/outline';
import LayoutWrapper from "../components/LayoutWrapper";
import Helper from "../helpers";
import config from "../config";
import {getSubscriptions} from "../Services/VenueService.ts";

const baseUrl = config.apiBaseUrl;

const ManageSubscription = () => {
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [userSubscription, setUserSubscription] = useState(null);
    const [upcomingPayments, setUpcomingPayments] = useState([]);
    const [availableSubscriptions, setAvailableSubscriptions] = useState([]);
    const [paymentHistory, setPaymentHistory] = useState([]);
    const [error, setError] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    const [showChangeSubscription, setShowChangeSubscription] = useState(false);
    const [selectedNewSubscription, setSelectedNewSubscription] = useState('');

    useEffect(() => {
        fetchSubscriptionData();
    }, []);

    const fetchSubscriptionData = async () => {
        setLoading(true);
        setError('');

        try {
            const token = await Helper.getCookie('wh-jwt');

            // Fetch current subscription
            const subResponse = await fetch(`${baseUrl}/api/subscription/user-subscription`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                credentials: 'include'
            });

            if (subResponse.ok) {
                const subData = await subResponse.json();
                // Update to match the new API response structure
                setSubscription(subData.subscription || subData);
                setUserSubscription(subData.userSubscription || { 
                    status: 'active', 
                    createdAt: subData.createdAt || new Date().toISOString(),
                    externalSubscriptionId: subData.externalSubscriptionId || null
                });
                setUpcomingPayments(subData.upcomingPayments || []);
            }

            // Fetch available subscriptions for changing
            try {
                const venueKey = await Helper.getUserVenueId();
                let subResultData = await getSubscriptions(venueKey);
                if (subResultData && subResultData.status === "no_subscriptions") {
                    setAvailableSubscriptions([]);
                } else if (subResultData && subResultData.items && subResultData.items.length > 0) {
                    // Custom plans belong to one client, so never offer them as a plan to switch to
                    setAvailableSubscriptions(subResultData.items.filter(item => !item.isCustomerSubscription));
                } else {
                    setAvailableSubscriptions([]);
                }
            } catch (error) {
                console.error('Error fetching available subscriptions:', error);
                setAvailableSubscriptions([]);
            }

            // Fetch payment history
            try {
                const venueKey = await Helper.getUserVenueId();
                const historyResponse = await fetch(`${baseUrl}/api/order/users/${venueKey}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include'
                });

                if (historyResponse.ok) {
                    const historyData = await historyResponse.json();
                    console.log('Payment history response:', historyData);
                    
                    // Sort payment history by date (newest first)
                    const sortedHistory = (historyData || []).sort((a, b) => {
                        const dateA = new Date(a.paymentDate);
                        const dateB = new Date(b.paymentDate);
                        return dateB - dateA; // Descending order (newest first)
                    });
                    
                    setPaymentHistory(sortedHistory);
                } else {
                    console.error('Failed to fetch payment history, status:', historyResponse.status);
                    setError('Unable to load payment history. Please try again later.');
                    setPaymentHistory([]);
                }
            } catch (error) {
                console.error('Error fetching payment history:', error);
                setError('Unable to load payment history. Please try again later.');
                setPaymentHistory([]);
            }

        } catch (error) {
            console.error('Error fetching subscription data:', error);
            // Only show error for critical failures, not for missing subscriptions
            if (error.message && !error.message.includes('subscription')) {
                setError('Unable to load some information. Please try again later.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        if (!window.confirm('Are you sure you want to cancel your subscription? This action cannot be undone.')) {
            return;
        }

        setActionLoading(true);
        setError('');

        try {
            const token = await Helper.getCookie('wh-jwt');
            
            const response = await fetch(`${baseUrl}/api/subscriptions/cancel`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    userSubscriptionId: userSubscription.id
                }),
                credentials: 'include'
            });

            if (response.ok) {
                await fetchSubscriptionData(); // Refresh data
                alert('Subscription cancellation request has been submitted successfully.');
            } else {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to cancel subscription');
            }
        } catch (error) {
            console.error('Error cancelling subscription:', error);
            setError(error.message || 'Failed to cancel subscription. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const handleChangeSubscription = async () => {
        if (!selectedNewSubscription) {
            setError('Please select a new subscription plan');
            return;
        }

        setActionLoading(true);
        setError('');

        try {
            const token = await Helper.getCookie('wh-jwt');
            const venueKey = await Helper.getUserVenueId();
            const userId = await Helper.getUserId();

            if (selectedNewSubscription === 'payg') {
                // Change to Pay As You Go using new endpoint
                const response = await fetch(`${baseUrl}/api/subscriptions/ChangeUserSubscription/${venueKey}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        UserId: userId,
                        NewSubscriptionId: 'payg', // or null for PAYG
                        CurrentUserSubscriptionId: userSubscription.id
                    }),
                    credentials: 'include'
                });

                if (response.ok) {
                    await fetchSubscriptionData();
                    setShowChangeSubscription(false);
                    alert('Successfully changed to Pay As You Go.');
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to change to Pay As You Go');
                }
            } else {
                // Change to different subscription using new endpoint
                const response = await fetch(`${baseUrl}/api/subscriptions/ChangeUserSubscription/${venueKey}`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        UserId: userId,
                        NewSubscriptionId: selectedNewSubscription,
                        CurrentUserSubscriptionId: userSubscription.id
                    }),
                    credentials: 'include'
                });

                if (response.ok) {
                    const data = await response.json();
                    
                    // Check if payment setup is required
                    const redirectUrl = data.mandateUrl || data.redirectUrl || data.redirect_url || data.billingRequestFlowUrl || data.url;
                    
                    if (redirectUrl) {
                        // Payment setup required - redirect to GoCardless
                        window.location.href = redirectUrl;
                    } else {
                        // No payment setup required - subscription changed successfully
                        await fetchSubscriptionData();
                        setShowChangeSubscription(false);
                        alert('Subscription changed successfully.');
                    }
                } else {
                    const errorData = await response.json();
                    throw new Error(errorData.message || 'Failed to change subscription');
                }
            }
        } catch (error) {
            console.error('Error changing subscription:', error);
            setError(error.message || 'Failed to change subscription. Please try again.');
        } finally {
            setActionLoading(false);
        }
    };

    const formatCycleType = (cycleType, cycleInterval) => {
        const types = {
            0: 'Daily',
            1: 'Weekly',
            2: 'Monthly',
            3: 'Quarterly',
            4: 'Yearly'
        };
        
        if (cycleInterval && cycleInterval > 1) {
            const typeName = types[cycleType] || 'Period';
            return `Every ${cycleInterval} ${typeName.toLowerCase()}s`;
        }
        
        return types[cycleType] || 'Custom';
    };

    const formatPaymentType = (paymentType) => {
        const types = {
            0: 'One-off',
            1: 'Subscription',
            2: 'Refund',
            3: 'Chargeback',
            4: 'Reversal',
            6: 'Site Subscription'
        };
        return types[paymentType] || 'Other';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        try {
            // Handle the specific format "2025-08-18T12:37:12.65" by ensuring it's a valid ISO string
            let dateToFormat = dateString;
            
            // If the date doesn't end with 'Z' and doesn't have a timezone, add 'Z' to make it UTC
            if (!dateString.includes('Z') && !dateString.includes('+') && !dateString.includes('-', 10)) {
                dateToFormat = dateString + 'Z';
            }
            
            return new Date(dateToFormat).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch (error) {
            console.error('Error formatting date:', dateString, error);
            return 'Invalid Date';
        }
    };

    if (loading) {
        return (
            <LayoutWrapper>
                <div className="min-h-screen flex items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
                </div>
            </LayoutWrapper>
        );
    }

    return (
        <LayoutWrapper>
            <div className="min-h-screen  text-white">
                <div className="container mx-auto py-8 px-4">
                    {/* Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-100 mb-2">Payment History</h1>
                        <p className="text-gray-400">View your subscription, payments, and transaction history</p>
                    </div>

                    {error && (
                        <div className="bg-red-900/50 border border-red-700 text-red-300 px-4 py-3 rounded-lg mb-6">
                            {error}
                        </div>
                    )}

                    {/* Current Subscription - Only show if there's a valid subscription */}
                    {subscription && userSubscription && (
                        <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mb-6">
                            <h2 className="text-xl font-semibold text-gray-100 mb-4 flex items-center">
                                <CreditCardIcon className="h-6 w-6 mr-2" />
                                Current Subscription
                            </h2>

                            <div className="grid md:grid-cols-2 gap-6">
                                <div>
                                    <div className="space-y-3">
                                        <div>
                                            <span className="text-gray-400">Plan:</span>
                                            <span className="ml-2 text-gray-100 font-medium">{subscription.name}</span>
                                            {subscription.isCustom && (
                                                <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-900/40 text-emerald-300 border border-emerald-700/40">
                                                    Set up for you
                                                </span>
                                            )}
                                        </div>
                                        {subscription.tokensPerCycle > 0 && (
                                            <div>
                                                <span className="text-gray-400">Tokens each payment:</span>
                                                <span className="ml-2 text-gray-100">{subscription.tokensPerCycle}</span>
                                            </div>
                                        )}
                                        {subscription.nextCollectionDate && (
                                            <div>
                                                <span className="text-gray-400">Next payment:</span>
                                                <span className="ml-2 text-gray-100 font-medium">
                                                    {new Date(subscription.nextCollectionDate).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                                </span>
                                            </div>
                                        )}
                                        <div>
                                            <span className="text-gray-400">Billing cycle:</span>
                                            <span className="ml-2 text-gray-100">{formatCycleType(subscription.cycleType, subscription.cycleInterval)}</span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Cost:</span>
                                            <span className="ml-2 text-gray-100 font-medium">
                                                £{subscription.cycleCost}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <div className="space-y-3">
                                        <div>
                                            <span className="text-gray-400">Status:</span>
                                            <span className={`ml-2 font-medium ${
                                                userSubscription.status === 'active' ? 'text-emerald-400' :
                                                userSubscription.status === 'cancelled' ? 'text-red-400' :
                                                userSubscription.status === 'pending' ? 'text-yellow-400' :
                                                'text-gray-400'
                                            }`}>
                                                {userSubscription.status.charAt(0).toUpperCase() + userSubscription.status.slice(1)}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-gray-400">Started:</span>
                                            <span className="ml-2 text-gray-100">{formatDate(userSubscription.createdAt)}</span>
                                        </div>
                                        {userSubscription.externalSubscriptionId && (
                                            <div>
                                                <span className="text-gray-400">Subscription ID:</span>
                                                <span className="ml-2 text-gray-100 text-sm font-mono">
                                                    {userSubscription.externalSubscriptionId}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Upcoming Payments */}
                    {upcomingPayments.length > 0 && (
                        <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mb-6">
                            <h2 className="text-xl font-semibold text-gray-100 mb-4 flex items-center">
                                <CalendarDaysIcon className="h-6 w-6 mr-2" />
                                Upcoming Payments
                            </h2>
                            <div className="space-y-3">
                                {upcomingPayments.map((payment, index) => (
                                    <div key={index} className="flex justify-between items-center p-3 bg-gray-800 rounded-lg">
                                        <div>
                                            <span className="text-gray-300">{formatDate(payment.dueDate)}</span>
                                            <span className="ml-2 text-sm text-gray-400">({payment.description})</span>
                                        </div>
                                        <span className="text-gray-100 font-medium">
                                            {payment.currency}{payment.amount}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Action Buttons - Only show if there are subscriptions available or user has an active subscription */}
                    {(availableSubscriptions.length > 0 || (subscription && userSubscription?.status === 'active')) && (
                        <div className="grid sm:grid-cols-2 gap-4 mb-6">
                            {availableSubscriptions.length > 0 && (
                                <button
                                    onClick={() => setShowChangeSubscription(true)}
                                    disabled={actionLoading}
                                    className="flex items-center justify-center px-4 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                                >
                                    <ArrowPathIcon className="h-5 w-5 mr-2" />
                                    Change Subscription
                                </button>
                            )}

                            {subscription && userSubscription?.status === 'active' && (
                                <button
                                    onClick={handleCancelSubscription}
                                    disabled={actionLoading}
                                    className="flex items-center justify-center px-4 py-3 bg-red-600 hover:bg-red-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
                                >
                                    {actionLoading ? (
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                                    ) : (
                                        <XMarkIcon className="h-5 w-5 mr-2" />
                                    )}
                                    Cancel Subscription
                                </button>
                            )}
                        </div>
                    )}

                    {/* Payment History */}
                    <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mb-6">
                        <h2 className="text-xl font-semibold text-gray-100 mb-4 flex items-center">
                            <CreditCardIcon className="h-6 w-6 mr-2" />
                            Transaction History
                        </h2>

                        {paymentHistory.length > 0 ? (
                            <div className="space-y-3">
                                {paymentHistory.map((payment, index) => (
                                    <div key={index} className="flex justify-between items-center p-4 bg-gray-800 rounded-lg">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-4">
                                                <div>
                                                    <div className="text-gray-100 font-medium">
                                                        {formatPaymentType(payment.paymentType)}
                                                    </div>
                                                    <div className="text-sm text-gray-400">
                                                        {formatDate(payment.paymentDate)}
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-gray-100 font-medium">
                                                        £{payment.amount?.toFixed(2) || 'N/A'}
                                                    </div>
                                                    <div className={`text-sm font-medium ${
                                                        payment.currentStatus === 'completed' ? 'text-emerald-400' :
                                                        payment.currentStatus === 'failed' || payment.currentStatus === 'cancelled' ? 'text-red-400' :
                                                        payment.currentStatus === 'processing' || payment.currentStatus === 'pending' || payment.currentStatus === 'active' || payment.currentStatus === 'Active' ? 'text-yellow-400' :
                                                        payment.currentStatus === 'disputed' ? 'text-orange-400' :
                                                        'text-gray-400'
                                                    }`}>
                                                        {payment.currentStatus?.charAt(0).toUpperCase() + payment.currentStatus?.slice(1) || ''}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-center py-8">
                                <InformationCircleIcon className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                                <p className="text-gray-400">No transaction history found.</p>
                            </div>
                        )}
                    </div>

                    {/* Change Subscription Modal */}
                    {showChangeSubscription && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
                                <h3 className="text-xl font-semibold text-gray-100 mb-4">Change Subscription</h3>
                                
                                <div className="space-y-4 mb-6">
                                    {availableSubscriptions.map((sub) => (
                                        <label key={sub.id} className="flex items-center p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750">
                                            <input
                                                type="radio"
                                                name="newSubscription"
                                                value={sub.id}
                                                checked={selectedNewSubscription === sub.id.toString()}
                                                onChange={(e) => setSelectedNewSubscription(e.target.value)}
                                                className="mr-3"
                                            />
                                            <div className="flex-1">
                                                <div className="text-gray-100 font-medium">{sub.name}</div>
                                                <div className="text-sm text-gray-400">
                                                    {formatCycleType(sub.cycleType, sub.cycleInterval)} • £{sub.cycleCost}
                                                </div>
                                            </div>
                                        </label>
                                    ))}
                                    
                                    <label className="flex items-center p-3 bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-750">
                                        <input
                                            type="radio"
                                            name="newSubscription"
                                            value="payg"
                                            checked={selectedNewSubscription === 'payg'}
                                            onChange={(e) => setSelectedNewSubscription(e.target.value)}
                                            className="mr-3"
                                        />
                                        <div className="flex-1">
                                            <div className="text-gray-100 font-medium">Pay As You Go</div>
                                            <div className="text-sm text-gray-400">No monthly commitment • Pay per session</div>
                                        </div>
                                    </label>
                                </div>

                                <div className="flex gap-3">
                                    <button
                                        onClick={() => {
                                            setShowChangeSubscription(false);
                                            setSelectedNewSubscription('');
                                            setError('');
                                        }}
                                        className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-medium"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleChangeSubscription}
                                        disabled={actionLoading || !selectedNewSubscription}
                                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium"
                                    >
                                        {actionLoading ? 'Processing...' : 'Change Plan'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </LayoutWrapper>
    );
};

export default ManageSubscription;
